import { CacheType } from '../types/CacheType';
import { GenericKeys, PropertyBagKeys } from '../types/DatapointEnums';
import { getUniquePropertyString } from '../utils/getUniquePropertyString';
import { DatapointOptions } from 'owa-analytics-actions';
import { getQueryStringParameter } from 'owa-querystring';
import { ActionSource } from 'owa-analytics-actions';
import { getSessionElapseTime } from '../utils/getSessionElapseTime';
import { scrubForPii } from 'owa-config';

const eventTypeQueryStringParam = 'track';
export function isEventQueryStringEnabled(eventType: string): boolean {
    let eventQueryString = getQueryStringParameter(eventTypeQueryStringParam);
    return (
        eventQueryString && (eventQueryString == '*' || eventQueryString.indexOf(eventType) > -1)
    );
}

export function isClientVerboseQueryStringEnabled(): boolean {
    return isEventQueryStringEnabled(VerbosePerfEventType);
}

export const VerbosePerfEventType = 'client_verbose';

type CustomDataTypes = string | number | boolean | Date;

export type CustomData = CustomDataTypes[] | { [key: string]: CustomDataTypes };

export interface DatapointProperty {
    value: CustomDataTypes;
    isInt: boolean;
}

let sequenceNumber = 0;

export abstract class AriaDatapoint {
    public static sessionOccurences: { [index: string]: number };
    eventName: string;
    options?: DatapointOptions;
    properties: { [index: string]: DatapointProperty };
    propertyBag: { [index: string]: CustomDataTypes };
    piiBag: { [index: string]: any };
    customDataIndex: number = 1;
    sessionOccurence: number;
    constructor(eventName?: string, extraCustomData?: CustomData, options?: DatapointOptions) {
        this.eventName = eventName && eventName.toString().replace('.', '_');
        this.properties = {};
        this.options = options;
        this.addCustomData(extraCustomData);

        const sessionElapseTime = getSessionElapseTime();
        if (sessionElapseTime) {
            this.addData(GenericKeys.sessionElapseTime, sessionElapseTime, true);
        }

        if (this.eventName) {
            this.addData(GenericKeys.eventName, this.eventName);
            this.updateSessionOccurences();
        }

        if (options?.actionSource) {
            this.addActionSource(options.actionSource);
        }

        if (options?.cosmosOnlyData) {
            this.addCosmosOnlyData(options.cosmosOnlyData);
        }

        this.addData(GenericKeys.sequenceNumber, sequenceNumber++);
    }
    public addCustomData(extraCustomData?: CustomData) {
        if (extraCustomData) {
            if (Array.isArray(extraCustomData)) {
                for (var ii = 0; ii < extraCustomData.length; ii++) {
                    this.addCustomProperty(
                        `owa_${(this.customDataIndex++).toString()}`,
                        extraCustomData[ii]
                    );
                }
            } else if (extraCustomData instanceof Object) {
                let props = Object.keys(extraCustomData);
                for (var jj = 0; jj < props.length; jj++) {
                    this.addCustomProperty(props[jj], extraCustomData[props[jj]]);
                }
            }
        }
    }
    public addCustomProperty(property: string, value: CustomDataTypes) {
        if (!this.propertyBag) {
            this.propertyBag = {};
        }
        property = getUniquePropertyString(this.propertyBag, property);
        this.propertyBag[property] = value;
    }
    public addPiiProperty(property: string, value: CustomDataTypes) {
        if (!this.piiBag) {
            this.piiBag = {};
        }
        property = getUniquePropertyString(this.piiBag, property);
        this.piiBag[property] = value;
    }

    public addCosmosOnlyData(value: string) {
        this.addDataWithPiiScrubbing(GenericKeys.extraData, value);
    }

    public addActionSource(value: ActionSource) {
        this.addData(GenericKeys.actionSource, value);
    }

    public addCache(value: CacheType) {
        this.addData(GenericKeys.cache, value);
    }

    public getEventType(): string {
        if (this.options) {
            if (this.options.isCosmosDatapoint) {
                return 'client_cosmos';
            } else if (this.options.isVerbose) {
                return VerbosePerfEventType;
            }
        }

        return 'client_event';
    }
    public getData(key: string) {
        var property = this.properties[key];
        return property?.value;
    }
    protected addDataWithPiiScrubbing(key: string, value: string) {
        this.addData(key, scrubForPii(value));
    }
    protected addData(key: string, value: CustomDataTypes, isInt?: boolean) {
        // The aria package can determine all of the property types except for ints. So allowing this to be set manually
        this.properties[key] = {
            value,
            isInt,
        };
    }
    protected addServerHeaders(headers: Headers) {
        this.addCustomProperty(PropertyBagKeys.feServer, headers.get('x-feserver'));
        this.addCustomProperty(PropertyBagKeys.beServer, headers.get('x-beserver'));
        this.addCustomProperty(PropertyBagKeys.backendStatus, headers.get('x-backendhttpstatus'));
        this.addCustomProperty(PropertyBagKeys.serverVersion, headers.get('x-owa-version'));
    }
    private updateSessionOccurences(): void {
        if (!AriaDatapoint.sessionOccurences) {
            AriaDatapoint.sessionOccurences = {};
        }

        var sessionOccurences = AriaDatapoint.sessionOccurences[this.eventName];
        this.sessionOccurence = AriaDatapoint.sessionOccurences[this.eventName] = sessionOccurences
            ? sessionOccurences + 1
            : 1;

        this.addData(GenericKeys.sessionOccurences, sessionOccurences, true);
    }
}
